import data from './ectd-headings.json';

/**
 * Where eCTD v3.2.2 and eCTD v4.0 differ under a heading the U.S. eCTD dossier prints
 * (structure beneath it, or v3.2.2 attributes v4.0 replaced with keywords) — each heading and
 * each Module 1 section is shown for the submission types in its own `applies_to` (the Module
 * 2-5 notes for all five U.S. medicinal types since Task DA3; the Module 1 sections and the 1.1 /
 * 1.18 notes per pathway, the drugs since Task DA3, A8).
 *
 * `ectd-headings.json` is a copy of `app/dossier/us_medicinal/rules/ectd_headings.json` (the
 * frontend image is built from `frontend-v2/` alone, so it cannot import the backend file);
 * `tests/test_us_ectd_headings.py` holds the two equal. Lockstep with the PDF: the line below
 * is `HeadingDifference.note()` there. The same file lists the further Module 1 sections
 * (`module1Sections`, Task M1; drugs Task DA3).
 */

/** An entry (a heading's note or a Module 1 section) shown for this submission type. */
function appliesTo(entry: { applies_to: string[] }, submissionType: string | null): boolean {
  return submissionType !== null && entry.applies_to.includes(submissionType);
}

/**
 * One quiet line naming both standards, labelled for the submitter's publisher (Task UI1: kept
 * out of the REGARA box), the same words as the PDF (`HeadingDifference.note()`).
 */
export function versionNoteText(v322: string, v40: string): string {
  return `eCTD publishing note (for your publisher): v3.2.2 ${v322}; v4.0 ${v40}`;
}

/**
 * The one key line shown once per dossier, directly above its first publishing note (the first
 * heading `ectdVersionNotes` returns: 1.1 Forms on all five pathways; Task UI2): the notes quote
 * FDA's CTOC verbatim, so the key says how to read the brackets and the (R) / (O) markers. The
 * same string as the PDF (`PUBLISHING_NOTE_KEY`, from the same JSON).
 */
export const PUBLISHING_NOTE_KEY: string = data.publishing_note_key;

/** Heading number → note line, for this submission type (the headings whose `applies_to` has it). */
export function ectdVersionNotes(submissionType: string | null): ReadonlyMap<string, string> {
  return new Map(
    data.headings
      .filter((h) => appliesTo(h, submissionType))
      .map((h) => [h.number, versionNoteText(h.v3_2_2, h.v4_0)]),
  );
}

/**
 * One of the further Module 1 sections (Task M1, `module_1_sections` in the same JSON): its
 * CTOC number and title, the dossier key that holds it, whether it shows `always` or only
 * `when_filled`, what belongs there (for this submission type; shown in the REGARA box,
 * Task UI1), the submitter's box, and whether it is always the submitter's (`submitter_only`: 1.1 Forms, 1.3.2 Field copy
 * certification). Lockstep with `_module_1_section` in
 * `backend/services/dossier/ectd_renderer/usa.py`.
 */
export type Module1Section = {
  number: string;
  title: string;
  key: string;
  shown: 'always' | 'when_filled';
  /** What belongs here: the text of the section's REGARA box (`belongs_for` in the PDF). */
  belongs: string;
  placeholder: string;
  /** An FDA form or a certification the applicant signs: always the box, never what a run wrote. */
  submitterOnly: boolean;
  /**
   * A section that is a table of rows, not prose (1.12.12, Task DB3): each column's row key and
   * heading, in order; null for a prose section.
   */
  table: { columns: { key: string; heading: string }[] } | null;
};

type SectionEntry = (typeof data.module_1_sections)[number] | typeof data.labeling_section;

/** One section entry of the JSON as this submission type shows it. */
function toSection(s: SectionEntry, submissionType: string): Module1Section {
  const belongs =
    typeof s.belongs === 'string'
      ? s.belongs
      : ((s.belongs as Record<string, string>)[submissionType] ?? '');
  const table = 'table' in s ? s.table : undefined;
  return {
    number: s.number,
    title: s.title,
    key: s.key,
    shown: s.shown === 'when_filled' ? 'when_filled' : 'always',
    belongs,
    placeholder: s.placeholder,
    submitterOnly: 'submitter_only' in s && s.submitter_only === true,
    table: table
      ? {
          columns: Object.entries(table.columns).map(([key, heading]) => ({
            key,
            heading: String(heading),
          })),
        }
      : null,
  };
}

/** The Module 1 sections this submission type shows (those whose `applies_to` has it), by number. */
export function module1Sections(submissionType: string | null): ReadonlyMap<string, Module1Section> {
  if (submissionType === null) return new Map();
  return new Map(
    data.module_1_sections
      .filter((s) => appliesTo(s, submissionType))
      .map((s) => [s.number, toSection(s, submissionType)]),
  );
}

/**
 * The single 1.14 Labeling section, with its REGARA box naming 21 CFR Part 201 and Part 610
 * Subpart G, for a pathway whose 1.14 is not split per CTOC (the biologics; `labeling_section` in
 * the JSON, Task QF); null otherwise. Lockstep with `labeling_section_for` in the backend rules.
 */
export function labelingSection(submissionType: string | null): Module1Section | null {
  if (submissionType === null || !appliesTo(data.labeling_section, submissionType)) return null;
  return toSection(data.labeling_section, submissionType);
}

/**
 * The further Module 1 sections under a shared heading that this submission type shows, in CTOC
 * order: a drug's 1.14 Labeling split per CTOC into 1.14.1 Draft labeling and, for the ANDA,
 * 1.14.3 Listed drug labeling (Task DB3). Empty when the pathway has none, and the heading keeps
 * its own single section. Lockstep with `subsections_for` in the backend rules.
 */
export function module1Subsections(
  sections: ReadonlyMap<string, Module1Section>,
  number: string,
): Module1Section[] {
  return [...sections.values()].filter((s) => s.number.startsWith(`${number}.`));
}

/** A section the run wrote: a prose object with a non-blank body (a placeholder counts). */
export function isFilledSection(prose: unknown): boolean {
  if (!prose || typeof prose !== 'object') return false;
  const body = (prose as { body?: unknown }).body;
  return typeof body === 'string' && body.trim() !== '';
}
